import React, { useEffect, useState, useRef } from "react";
import "../../../../../css/RecruitmentScreen/Sections.css";
import FormField from "../FormField"; // Import the reusable FormField component
import SignatureCanvas from "react-signature-canvas";
import { writeDataS11 } from "../../../../../backend/Sections/Section11/writeDataS11"; // Import the writeDataS11 function
import { readDataS11 } from "../../../../../backend/Sections/Section11/readDataS11"; // Import the readDataS11 function
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import UseDatepicker from "../Datepicker";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";
import { shouldBypassValidation } from "../../Utils/validationPolicy";
import { validateSection11 } from "../Validation/section11";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import { formatDateValue } from "../Utils/dateFormatting";
import SecondaryButton from "../../../../DesignSystem/Buttons/SecondaryButton";

const Section11 = ({ onValidationStatusChange }) => {
  const [formData, setFormData] = useState({
    fullname: "",
    candidateSignature: "",
    date: new Date(),
    printName: "",
    printNameSignature: "",
    printNameDate: new Date(),
    // directorSignature: "",
    // directorName: "",
  });

  const [error, setError] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const sectionTopRef = useRef(null);

  const advisorSigCanvasRef = useRef([]);
  const printNameSigCanvasRef = useRef([]);
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());

  useEffect(() => {
    const removeListener = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());

    return () => removeListener();
  }, []);

  const clearSignaturePad = (sigCanvasRef, fieldName) => {
    sigCanvasRef.current?.clear();
    setFormData((prev) => ({ ...prev, [fieldName]: "" }));
  };

  const saveSignature = (sigCanvasRef, fieldName) => {
    const signatureDataUrl = sigCanvasRef.current?.toDataURL() || "";
    setFormData((prev) => ({ ...prev, [fieldName]: signatureDataUrl }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (formData.fullname.trim() === "") {
      newErrors.fullname = "This field is required.";
    }
    if (formData.candidateSignature === "") {
      newErrors.candidateSignature = "This field is required.";
    }
    if (formData.date === "") {
      newErrors.date = "This field is required.";
    }
    // if (formData.directorSignature === "") {
    //   newErrors.directorSignature = "This field is required.";
    // }
    // if (formData.directorName === "") {
    //   newErrors.directorName = "This field is required.";
    // }
    setError(newErrors);
    errorTrackerV2.setSectionErrors("section11", newErrors);
    onValidationStatusChange(Object.keys(newErrors).length === 0);

    return Object.keys(error).length === 0;
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });

    const ok = validateSection11({
      formData,
      setError,
      onValidationStatusChange,
    });

    // Save data regardless of validation status
    await writeDataS11(formData);

    // Show appropriate alert based on validation result
    if (ok || shouldBypassValidation(auth.currentUser?.uid)) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await readDataS11();
      if (data) {
        setFormData({
          ...data,
          date: data.date ? new Date(data.date.seconds * 1000) : "",
          printNameDate: data.printNameDate
            ? new Date(data.printNameDate.seconds * 1000)
            : "",
        });
        onValidationStatusChange(true);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    advisorSigCanvasRef.current.fromDataURL(formData.candidateSignature);
    printNameSigCanvasRef.current.fromDataURL(formData.printNameSignature);
  }, [formData]);

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onDismiss={() => setAlertMessage("")}
        />
      )}
      <h3>Section 11 – Declaration</h3>
      <div className="Content">Reference Consent</div>
      <div className="Content-Small">
        My signature below authorises my former employers and referees to
        release information regarding my employment record with Rosemount
        Financial Solutions (IFA) Ltd and to provide any additional information
        that may be necessary for my application for employment with them.
      </div>
      <div className="Content-Small ">
        I understand and acknowledge that any offer of appointment is
        conditional to Rosemount Financial Solutions (IFA) Ltd being completely
        satisfied with the information provided by these references.
      </div>
      {/* <div className="Content">Referee Name: Grace Furner</div> */}
      <div className="Content">
        Company Name: Rosemount Financial Solutions (IFA) Ltd
      </div>
      <div className="Content">Company FCA Number: 535515</div>

      <div className="input-group-row">
        <div className="Content">
          Print Name<span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <InputField
            name="printName"
            placeholder="Print Name"
            value={formData.printName}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          Signature<span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="form-row" style={{ alignItems: "center" }}>
          <SignatureCanvas
            ref={(el) => (printNameSigCanvasRef.current = el)}
            penColor="#890304"
            canvasProps={{
              className: "sigCanvas",
            }}
            onEnd={() =>
              saveSignature(printNameSigCanvasRef, "printNameSignature")
            }
          />
          <SecondaryButton
            label="Clear"
            onClick={() =>
              clearSignaturePad(printNameSigCanvasRef, "printNameSignature")
            }
            disabled={isSubmitted}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          Date<span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <DateField
            name="date"
            dateFormat={"dd/MM/yyyy"}
            placeholderText="Date of Signature"
            value={formData.printNameDate}
            onChange={(date) =>
              handleChange({
                target: { name: "printNameDate", value: date },
              })
            }
          />
        </div>
      </div>

      <div className="separator" />

      <div className="Content">IMPORTANT INFORMATION </div>
      <div className="Content">
        It may be a criminal offence to provide false or misleading information
        to the Financial Conduct Authority or Rosemount Financial Solutions(IFA)
        Ltd. It is, therefore, vital that all questions in this application form
        have been answered fully and accurately.
      </div>
      <div className="Content">
        I understand that any information contained in the application or
        otherwise supplied by me/us may be shared with Rosemount Financial
        Solutions (IFA) Ltd for the purposes of processing this application and
        the Appointed Representative (if applicable) I am joining.
      </div>
      <div className="Content">I confirm that:</div>
      <ul>
        <li className="Content-Small">
          The information given in this application is accurate and complete,
          and all relevant details have been disclosed.
        </li>
        <li className="Content-Small">
          I understand that the information disclosed may be used as the basis
          of deciding if I will be offered a contract with Rosemount Financial
          Solutions (IFA) Ltd.
        </li>
        <li className="Content-Small">
          I give my permission for Rosemount Financial Solutions (IFA) Ltd to
          undertake a credit search with a licensed credit reference agency and
          acknowledge that the credit reference agency will keep a record of
          this search and may share this with other businesses.
        </li>
        <li className="Content-Small">
          I give my permission for Rosemount Financial Solutions (IFA) Ltd to
          take up any references that it considers appropriate in order to
          consider this application and I authorise that source to release the
          information requested. This may include con tacting any Lender, Bank,
          Building Society, Accountant, Solicitor, past and present Employers,
          Unemployment Benefit Office or any other relevant party.
        </li>
        <li className="Content-Small">
          I understand that if there is any material change to my circumstances
          after the date of this application, I will promptly inform Rosemount
          Financial Solutions (IFA) Ltd.
        </li>
        <li className="Content-Small">
          I understand I will be invited to attend training courses at a place
          advised by Rosemount Financial Solutions (IFA) Ltd and that this
          invitation does not imply a commitment from Rosemount Financial
          Solutions (IFA) Ltd to offer a contract upon completion of the
          training; nor does it imply a commitment on your part to proceed with
          your application. However, successful completion of the training
          courses is a precondition of the offer of a contract.
        </li>
        <li className="Content-Small">
          I confirm I will enter into an agreed repayment schedule, or full and
          final settlement, with any Financial Services Company, to repay any
          related Financial Services debt.
        </li>
        <li className="Content-Small">
          I understand I am not authorised to represent Rosemount Financial
          Solutions (IFA) Ltd in any way until written confirmation has been
          provided by Rosemount Financial Solutions (IFA) Ltd.
        </li>
        <li className="Content-Small">
          If authorisation is given by Rosemount Financial Solutions (IFA) Ltd,
          I will, at all times, comply with the FCA rules, and Rosemount
          Financial Solutions (IFA) Ltd policies and procedures.
        </li>
        <li className="Content-Small">
          I understand that Rosemount Financial Solutions (IFA) Ltd will conduct
          an identity check from the information I have provided on my
          application form.
        </li>
        <li className="Content-Small">
          I understand that Rosemount Financial Solutions (IFA) Ltd may request
          a Disclosure and Barring Service check to be conducted following the
          information I have provided on my application form.
        </li>
        <li className="Content-Small">
          I understand that Rosemount Financial Solutions (IFA) Ltd will obtain
          a Disclosure Scotland check following the information provided on my
          application form.
        </li>
      </ul>
      <div className="Content">Data Protection</div>
      <div className="Content">
        Under the Data Protection Act 1988 we have to tell you who’s responsible
        for deciding how your personal information will be used.The information
        you provide on this form including any sensitive personal details for
        example criminal convictions, will be used by Rosemount Financial
        Solutions (IFA) Ltd to assess your suitability for the contract you have
        applied for.
      </div>
      <div className="Content">
        This may include undertaking credit checks and seeking references for
        the purpose of verification, identification and in accordance with
        Rosemount Financial Solutions (IFA) Ltd regulatory requirements to
        ensure that you are fit and proper to perform the activities permitted
        under the contract. We may share your relevant personal information with
        other companies that Rosemount Financial Solutions (IFA) Ltd are or
        become associated with and agents acting on behalf of Rosemount
        Financial Solutions (IFA) Ltd to comply with our regulatory obligations
        and for ongoing administration, management information and marketing
        purposes. For the avoidence of doubt this may include other Old Mutual
        group entities.
      </div>
      <div className="Content">
        The personal data you provide will be used and stored in accordance with
        the Data Protection Act 1998.To understand exactly what data we hold,
        why we hold it and your rights under the Data Protection Act 1998 our
        Fair Processing Notice can be viewed at www.rosemountfs-ifa.co.uk please
        view Fair Processing Notice link.
      </div>

      <div className="Content">Declaration of this application</div>
      <div className="Content">
        I accept the terms and conditions of this application to join Rosemount
        Financial Solutions (IFA) Ltd.
      </div>
      <div className="input-group-row">
        <div className="Content">
          Full name of person submitting the application
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <InputField
            name="fullname"
            placeholder="Full Name"
            value={formData.fullname}
            error={error.fullname}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          Signature<span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="form-row" style={{ alignItems: "center" }}>
          <SignatureCanvas
            ref={(el) => (advisorSigCanvasRef.current = el)}
            penColor="#890304"
            canvasProps={{
              className: "sigCanvas",
            }}
            onEnd={() =>
              saveSignature(advisorSigCanvasRef, "candidateSignature")
            }
          />
          {error.candidateSignature && (
            <span className="error-message">{error.candidateSignature}</span>
          )}
          <SecondaryButton
            label="Clear"
            onClick={() =>
              clearSignaturePad(advisorSigCanvasRef, "candidateSignature")
            }
            disabled={isSubmitted}
          />
        </div>
      </div>
      <div className="sizebox" />
      <div className="input-group-row">
        <div className="Content">
          Date<span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <DateField
            name="date"
            dateFormat={"dd/MM/yyyy"}
            placeholderText="Date of Signature"
            value={formData.date}
            err={error.date}
            onChange={(date) =>
              handleChange({
                target: { name: "date", value: date },
              })
            }
          />
        </div>
      </div>

      {/* <div className="separator" /> */}
      {/* <div className="form-row">
        <label>
          Signature of Rosemount Director/ Principle of Appointed Representative
          Firm
        </label>
        <div className="input-group">
          <SignatureCanvas
            ref={(el) => (officerSigCanvasRef.current = el)}
            penColor="#fffeea"
            canvasProps={{
              className: "sigCanvas",
            }}
            onEnd={() => officerSigCanvasRef.current.clear()}
          />
          {error.directorSignature && (
            <span className="error-message">{error.directorSignature}</span>
          )}
        </div>
      </div>
      <div className="sizebox" />
      <div className="form-row">
        <label>
          Name of your Rosemount Director/Principle of Appointed Representative
          Firm
        </label>
        <div className="input-group">
          <FormField
            name="directorName"
            placeholder=""
            value={formData.directorName}
            error={error.directorName}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="sizebox" /> */}
      <div className="Content-Small">
        Please note - If you add an e-signature we will require a copy of the
        document with your actual signature on it.
      </div>
      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section11;
